/* ─────────────────────────────────────────────────────────────
   tlpc-global-script.js — theme-wide behaviour shared by every section.

   On-view reveal
   --------------
   One IntersectionObserver for the whole page. Sections opt in with a
   single `data-reveal` attribute; the CSS lives in tlpc-global-style.css.
   Nothing is authored per section.

   - threshold 0 + rootMargin -35% bottom: height-independent, so a section
     taller than the viewport still fires. Never a ratio threshold.
   - Each element is observed individually, so a group staggers on its own
     and always finishes before the section scrolls past.
   - Stagger is grouped per .shopify-section, 90ms step, capped at 450ms.
   - Loaded with `defer`, so the DOM is parsed by the time this runs. No
     DOMContentLoaded, no readiness polling.
   ───────────────────────────────────────────────────────────── */
(function () {
  var STEP = 90;
  var CAP = 450;
  var READY = 'tlpc-reveal-ready';

  var root = document.documentElement;

  // Cancels the CSS failsafe. First statement that touches the DOM, so a
  // throw further down still leaves the failsafe armed.
  root.classList.add(READY);

  function show(el) {
    el.classList.add('is-visible');
  }

  var reduced = window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var supported = 'IntersectionObserver' in window;

  var observer = (!reduced && supported)
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          show(entry.target);
          observer.unobserve(entry.target);
        });
      }, { threshold: 0, rootMargin: '0px 0px -35% 0px' })
    : null;

  /**
   * Register every not-yet-initialised [data-reveal] under `scope`.
   * Safe to call repeatedly — initialised elements are skipped.
   */
  function register(scope) {
    var targets = (scope || document).querySelectorAll('[data-reveal]:not(.tlpc-reveal-init)');
    if (!targets.length) return;

    // Stagger index per section, so sections don't inherit each other's delay.
    var counts = new Map();

    targets.forEach(function (el) {
      el.classList.add('tlpc-reveal-init');

      // No JS-driven reveal available, or motion is not wanted: show it now.
      if (!observer) {
        show(el);
        return;
      }

      // Not rendered (a display:none tab panel or card). It can never
      // intersect, so animating it would leave it invisible when its tab is
      // opened. Reveal it outright.
      if (!el.getClientRects().length) {
        show(el);
        return;
      }

      var section = el.closest('.shopify-section') || document;
      var i = counts.get(section) || 0;
      counts.set(section, i + 1);

      if (i) el.style.transitionDelay = Math.min(i * STEP, CAP) + 'ms';
      observer.observe(el);
    });
  }

  register(document);

  // Theme editor swaps section markup in and out constantly.
  document.addEventListener('shopify:section:load', function (event) {
    register(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    if (!observer) return;
    event.target.querySelectorAll('[data-reveal]').forEach(function (el) {
      observer.unobserve(el);
    });
  });

  window.TLPC = window.TLPC || {};
  window.TLPC.reveal = register;

  /* ───────────────────────────────────────────────────────────
     Swiper handoff
     ---------------
     This file is deferred after swiper-bundle.min.js, and deferred
     scripts run in document order — so window.Swiper is already
     defined by the time we get here. Settle the queue the <head>
     stub collected from the inline section scripts, then answer
     later callers straight away.

     If the bundle failed to load, resolve with null. Sections check
     the value, so a missing library leaves a static, readable list
     instead of a half-built carousel.
     ─────────────────────────────────────────────────────────── */
  var Swiper = window.Swiper || null;
  var queue = TLPC._swiperQueue || [];

  TLPC.swiper = function () { return Promise.resolve(Swiper); };
  queue.forEach(function (resolve) { resolve(Swiper); });
  TLPC._swiperQueue = null;
}());
